import React, { useEffect, useState } from 'react';
import { paymentMethodsApi } from '../lib/api';
import { Plus, Pencil, Trash2, Wallet, CreditCard, Bitcoin, FileText, Settings2, Star, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const methodIcons = {
  platega: CreditCard,
  crypto: Bitcoin,
  manual: FileText,
  telegram_stars: Star,
  cryptobot: Bot,
};

const methodLabels = {
  platega: 'Platega.io (СБП + Крипто)',
  crypto: 'Криптовалюта (ручной)',
  manual: 'Ручной платеж',
  telegram_stars: 'Telegram Stars',
  cryptobot: 'CryptoBot',
};

const methodDescriptions = {
  platega: 'СБП (QR) и криптовалюта через Platega.io — в боте выбор из двух кнопок',
  crypto: 'Ручная проверка криптоплатежей',
  manual: 'Перевод на карту с ручным подтверждением',
  telegram_stars: 'Встроенные платежи Telegram Stars',
  cryptobot: 'Криптоплатежи через @CryptoBot',
};

const initialFormState = {
  type: '',
  name: '',
  config: {},
  instructions: '',
};

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const fetchMethods = async () => {
    try {
      const res = await paymentMethodsApi.getAll();
      setMethods(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки платежных систем');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMethods();
  }, []);

  const openCreateModal = () => {
    setEditingMethod(null);
    setFormData(initialFormState);
    setModalOpen(true);
  };

  const openEditModal = (method) => {
    setEditingMethod(method);
    setFormData({
      type: method.type,
      name: method.name,
      config: method.config || {},
      instructions: method.instructions || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingMethod) {
        await paymentMethodsApi.update(editingMethod.id, formData);
        toast.success('Платежная система обновлена');
      } else {
        await paymentMethodsApi.create(formData);
        toast.success('Платежная система добавлена');
      }
      setModalOpen(false);
      fetchMethods();
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (method) => {
    if (!window.confirm(`Удалить "${method.name}"?`)) return;

    try {
      await paymentMethodsApi.delete(method.id);
      toast.success('Платежная система удалена');
      fetchMethods();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleToggleActive = async (method) => {
    try {
      await paymentMethodsApi.update(method.id, { is_active: !method.is_active });
      fetchMethods();
      toast.success(method.is_active ? 'Платежная система отключена' : 'Платежная система включена');
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const updateConfig = (key, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value },
    });
  };

  const getMethodColor = (type) => {
    const colors = {
      platega: 'bg-blue-500/10 text-blue-400',
      crypto: 'bg-orange-500/10 text-orange-400',
      manual: 'bg-purple-500/10 text-purple-400',
      telegram_stars: 'bg-yellow-500/10 text-yellow-400',
      cryptobot: 'bg-cyan-500/10 text-cyan-400',
    };
    return colors[type] || 'bg-zinc-500/10 text-zinc-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="payment-methods-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Платежные системы</h1>
          <p className="text-zinc-500">Настройка способов оплаты для бота</p>
        </div>
        <Button 
          onClick={openCreateModal} 
          className="glow-primary"
          data-testid="add-payment-method-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Добавить способ
        </Button>
      </div>

      {methods.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {methods.map((method) => {
            const Icon = methodIcons[method.type] || Wallet;
            return (
              <div 
                key={method.id} 
                className="card card-hover"
                data-testid={`payment-method-card-${method.id}`}
              >
                <div className="card-header">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getMethodColor(method.type)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{method.name}</p>
                      <p className="text-xs text-zinc-500">{methodLabels[method.type]}</p>
                    </div>
                  </div>
                  <Switch
                    checked={method.is_active}
                    onCheckedChange={() => handleToggleActive(method)}
                    data-testid={`toggle-method-${method.id}`}
                  />
                </div>
                <div className="card-content">
                  {method.type === 'platega' && (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Merchant ID:</span>
                        <code className="text-emerald-400 font-mono text-xs">
                          {method.config?.merchant_id ? '✓ настроен' : '✗ не задан'}
                        </code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">API Secret:</span>
                        <code className="text-emerald-400 font-mono text-xs">
                          {method.config?.secret_set ? '✓ настроен' : '✗ не задан'}
                        </code>
                      </div>
                    </div>
                  )}
                  {method.type === 'telegram_stars' && (
                    <div className="text-sm text-zinc-400">
                      <p>Оплата через встроенную валюту Telegram</p>
                      <p className="text-xs text-zinc-500 mt-1">Токен бота настраивается в "Настройки бота"</p>
                    </div>
                  )}
                  {method.type === 'cryptobot' && (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Валюта:</span>
                        <span className="text-zinc-300">{method.config?.asset || 'USDT'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Курс $:</span>
                        <span className="text-zinc-300">
                          {method.config?.usd_rate
                            ? `1$ = ${method.config.usd_rate}₽`
                            : '✗ не задан'}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500">API токен настраивается в "Настройки бота"</p>
                    </div>
                  )}
                  {method.type === 'crypto' && (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Кошелек:</span>
                        <code className="text-emerald-400 font-mono text-xs truncate max-w-32">
                          {method.config?.wallet || 'не задан'}
                        </code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Сеть:</span>
                        <span className="text-zinc-300">{method.config?.network || 'не задана'}</span>
                      </div>
                    </div>
                  )}
                  {method.type === 'manual' && method.instructions && (
                    <p className="text-sm text-zinc-400 line-clamp-2">{method.instructions}</p>
                  )}
                  <div className="flex items-center gap-2 pt-4 mt-4 border-t border-white/5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(method)}
                      className="flex-1"
                      data-testid={`edit-method-${method.id}`}
                    >
                      <Settings2 className="w-3 h-3 mr-1" />
                      Настроить
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(method)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-method-${method.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Wallet className="empty-state-icon" />
            <p className="mb-4">Платежные системы не настроены</p>
            <Button onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Добавить первый способ оплаты
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMethod ? 'Настройка платежной системы' : 'Добавить платежную систему'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Тип платежной системы</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value, config: {} })}
                  disabled={!!editingMethod}
                >
                  <SelectTrigger className="bg-[#121212] border-white/10" data-testid="method-type-select">
                    <SelectValue placeholder="Выберите тип" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="telegram_stars">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-400" />
                        Telegram Stars
                      </div>
                    </SelectItem>
                    <SelectItem value="cryptobot">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-cyan-400" />
                        CryptoBot
                      </div>
                    </SelectItem>
                    <SelectItem value="platega">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-400" />
                        Platega.io (Карты, СБП)
                      </div>
                    </SelectItem>
                    <SelectItem value="crypto">
                      <div className="flex items-center gap-2">
                        <Bitcoin className="w-4 h-4 text-orange-400" />
                        Криптовалюта (ручной)
                      </div>
                    </SelectItem>
                    <SelectItem value="manual">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-400" />
                        Ручной платеж
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formData.type && (
                  <p className="text-xs text-zinc-500 mt-1">{methodDescriptions[formData.type]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="name">Название</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Оплата картой / USDT / Stars"
                  required
                  data-testid="method-name-input"
                />
              </div>

              {/* Telegram Stars Config */}
              {formData.type === 'telegram_stars' && (
                <div className="p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                  <h4 className="text-sm font-medium text-yellow-400 mb-2">Telegram Stars</h4>
                  <p className="text-xs text-zinc-400">
                    Для работы со Stars необходимо указать токен бота в разделе "Настройки бота".
                    Stars используются для цифровых товаров и услуг внутри Telegram.
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Цены в Stars указываются в тарифах (поле "Цена в Stars").
                  </p>
                </div>
              )}

              {/* CryptoBot Config */}
              {formData.type === 'cryptobot' && (
                <div className="space-y-4 p-4 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                  <h4 className="text-sm font-medium text-cyan-400">Настройки CryptoBot</h4>
                  <p className="text-xs text-zinc-400 mb-3">
                    API токен CryptoBot указывается в разделе "Настройки бота".
                    Получить токен: @CryptoBot → Crypto Pay → Create App
                  </p>
                  <div>
                    <Label htmlFor="asset">Криптовалюта для приема</Label>
                    <Select
                      value={formData.config?.asset || 'USDT'}
                      onValueChange={(value) => updateConfig('asset', value)}
                    >
                      <SelectTrigger className="bg-[#121212] border-white/10" data-testid="cryptobot-asset">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USDT">USDT</SelectItem>
                        <SelectItem value="TON">TON</SelectItem>
                        <SelectItem value="BTC">BTC</SelectItem>
                        <SelectItem value="ETH">ETH</SelectItem>
                        <SelectItem value="LTC">LTC</SelectItem>
                        <SelectItem value="BNB">BNB</SelectItem>
                        <SelectItem value="TRX">TRX</SelectItem>
                        <SelectItem value="USDC">USDC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="usd_rate">Курс доллара (₽ за 1 USDT/$)</Label>
                    <Input
                      id="usd_rate"
                      type="number"
                      min="1"
                      step="0.01"
                      value={formData.config?.usd_rate ?? ''}
                      onChange={(e) => updateConfig('usd_rate', e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="например 90"
                      className="bg-[#121212] border-white/10"
                      data-testid="cryptobot-usd-rate"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Сумма в {formData.config?.asset || 'USDT'} = цена тарифа (₽) ÷ этот курс.
                      Пример: 900₽ при курсе 90 → 10 USDT.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="allow_anonymous"
                      checked={formData.config?.allow_anonymous !== false}
                      onCheckedChange={(checked) => updateConfig('allow_anonymous', checked)}
                    />
                    <Label htmlFor="allow_anonymous" className="text-sm">Разрешить анонимные платежи</Label>
                  </div>
                </div>
              )}

              {/* Platega.io Config */}
              {formData.type === 'platega' && (
                <div className="space-y-4 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <h4 className="text-sm font-medium text-blue-400">Настройки Platega.io API</h4>
                  <div>
                    <Label htmlFor="merchant_id">Merchant ID</Label>
                    <Input
                      id="merchant_id"
                      value={formData.config?.merchant_id || ''}
                      onChange={(e) => updateConfig('merchant_id', e.target.value)}
                      placeholder="Ваш Merchant ID"
                      data-testid="platega-merchant-id"
                    />
                  </div>
                  <div>
                    <Label htmlFor="secret">API Secret</Label>
                    <Input
                      id="secret"
                      type="password"
                      value={formData.config?.secret || ''}
                      onChange={(e) => updateConfig('secret', e.target.value)}
                      placeholder={formData.config?.secret_set ? 'Секрет сохранён — оставьте пустым, чтобы не менять' : 'Ваш API Secret'}
                      data-testid="platega-secret"
                    />
                  </div>
                  <div>
                    <Label htmlFor="success_url">URL успешной оплаты</Label>
                    <Input
                      id="success_url"
                      value={formData.config?.success_url || ''}
                      onChange={(e) => updateConfig('success_url', e.target.value)}
                      placeholder="https://t.me/your_bot"
                      data-testid="platega-success-url"
                    />
                  </div>
                  <div>
                    <Label htmlFor="failed_url">URL неудачной оплаты</Label>
                    <Input
                      id="failed_url"
                      value={formData.config?.failed_url || ''}
                      onChange={(e) => updateConfig('failed_url', e.target.value)}
                      placeholder="https://t.me/your_bot"
                      data-testid="platega-failed-url"
                    />
                  </div>
                </div>
              )}

              {/* Crypto Config */}
              {formData.type === 'crypto' && (
                <div className="space-y-4 p-4 rounded-lg bg-orange-500/5 border border-orange-500/20">
                  <h4 className="text-sm font-medium text-orange-400">Настройки криптовалюты</h4>
                  <div>
                    <Label htmlFor="wallet">Адрес кошелька</Label>
                    <Input
                      id="wallet"
                      value={formData.config?.wallet || ''}
                      onChange={(e) => updateConfig('wallet', e.target.value)}
                      placeholder="TXxxxxxxxxxxxxxxxxxxxx"
                      data-testid="crypto-wallet"
                    />
                  </div>
                  <div>
                    <Label htmlFor="network">Сеть</Label>
                    <Select
                      value={formData.config?.network || ''}
                      onValueChange={(value) => updateConfig('network', value)}
                    >
                      <SelectTrigger className="bg-[#121212] border-white/10" data-testid="crypto-network">
                        <SelectValue placeholder="Выберите сеть" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRC20">USDT TRC20</SelectItem>
                        <SelectItem value="ERC20">USDT ERC20</SelectItem>
                        <SelectItem value="BEP20">USDT BEP20</SelectItem>
                        <SelectItem value="BTC">Bitcoin</SelectItem>
                        <SelectItem value="ETH">Ethereum</SelectItem>
                        <SelectItem value="TON">TON</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="currency">Криптовалюта</Label>
                    <Input
                      id="currency"
                      value={formData.config?.currency || ''}
                      onChange={(e) => updateConfig('currency', e.target.value)}
                      placeholder="USDT / BTC / ETH"
                      data-testid="crypto-currency"
                    />
                  </div>
                </div>
              )}

              {/* Manual Config */}
              {formData.type === 'manual' && (
                <div className="space-y-4 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <h4 className="text-sm font-medium text-purple-400">Настройки ручного платежа</h4>
                  <div>
                    <Label htmlFor="card_number">Номер карты / Реквизиты</Label>
                    <Input
                      id="card_number"
                      value={formData.config?.card_number || ''}
                      onChange={(e) => updateConfig('card_number', e.target.value)}
                      placeholder="2200 **** **** 1234"
                      data-testid="manual-card"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank">Банк</Label>
                    <Input
                      id="bank"
                      value={formData.config?.bank || ''}
                      onChange={(e) => updateConfig('bank', e.target.value)}
                      placeholder="Сбербанк / Тинькофф"
                      data-testid="manual-bank"
                    />
                  </div>
                  <div>
                    <Label htmlFor="recipient">Получатель</Label>
                    <Input
                      id="recipient"
                      value={formData.config?.recipient || ''}
                      onChange={(e) => updateConfig('recipient', e.target.value)}
                      placeholder="Иван И."
                      data-testid="manual-recipient"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="instructions">Инструкция для пользователя</Label>
                <Textarea
                  id="instructions"
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  placeholder="Опишите как пользователю оплатить..."
                  rows={3}
                  className="bg-[#121212] border-white/10"
                  data-testid="method-instructions"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={submitting || !formData.type} data-testid="save-method-btn">
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
